import { wrappedFetch } from '../helpers/fetch';
import { clearAlerts, startCheckout, setCheckoutOverlay } from './ui';
import { loadListingDataIntoState } from '../../listing/actions/general';
import { EMBED_TYPE_NO_COMPONENT, EMBED_CART_BEHAVIOR_PIN_TO_TOP } from '../../checkout/reducers/ui';
import { setSelectedDate, setSelectedChildEvent } from '../views/series/actions/event-series';
import { setCurrentMonth, setShowPyosPicker, setShowBestAvailablePicker } from './ui';
import { clearChildEventDetails } from './event';
import { submitPresaleCoupon, validatePresaleCoupon, validateCoupon } from '@checkout-v/coupon/actions/coupon';
import { navigate } from '@checkout-h/navigationService';

export function setDynamicListing(data) {
  return { type: 'SET_DYNAMIC_LISTING', data };
}
const loadedDynamicListing = () => {
  return { type: 'LOADED_DYNAMIC_LISTING' };
}
const setDynamicListingId = val => {
  return { type: 'SET_DYNAMIC_LISTING_ID', val };
}
const setDynamicListingSlug = val => {
  return { type: 'SET_DYNAMIC_LISTING_SLUG', val };
}
const setDynamicListingEventId = val => {
  return { type: 'SET_DYNAMIC_LISTING_EVENT_ID', val };
}
const setDynamicListingInfo = val => {
  return { type: 'SET_DYNAMIC_LISTING_INFO', val };
}
const setDynamicListingUiConfig = val => {
  return { type: 'SET_DYNAMIC_LISTING_UI_CONFIG', val };
}
const setDynamicListingMaintenanceMode= val => {
  return { type: 'SET_DYNAMIC_LISTING_MAINTENANCE_MODE', val };
}
export const setDynamicListingLoading = val => {
  return { type: 'SET_DYNAMIC_LISTING_LOADING', val };
}

export const loadListingDataIntoCheckout = (listingSlug, eventId) => {
  return async (dispatch, getState) => {
    let returnResult = false;

    const state = getState();

    try {
      const url = `${state.ui.apiUrl}/listings/${listingSlug}/${eventId}/info`;
      const fetchOpts = {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      };

      const response = await wrappedFetch(url, fetchOpts);

      if (response.ok) {
        const body = await response.json();

        const { uiConfig, ...listingInfo} = body;

        uiConfig.listings.embedCartBehavior = EMBED_CART_BEHAVIOR_PIN_TO_TOP;

        const listingId = listingInfo.event.attributes.listingId;
        const eventId   = Object.keys(listingInfo.event.attributes.events)[0];
        const maintenanceMode = listingInfo.maintenance.maintenanceMode;
        const { appliedPresaleCoupons } = state.coupon;
        // Snapshot codes before loading so we can re-validate them against the new event
        const activeDiscountCode = state.coupon.code;
        const activeDiscountShowSuccess = state.coupon.showSuccess;
        const activeAccessCode = state.coupon.accessCode;

        dispatch(clearChildEventDetails(null, true));
        dispatch(setSelectedChildEvent(null));
        dispatch(setSelectedDate(''));
        dispatch(setCurrentMonth(null));
        dispatch(setShowPyosPicker(false));
        dispatch(setShowBestAvailablePicker(false));

        dispatch(loadListingDataIntoState(
          listingId,
          eventId,
          EMBED_TYPE_NO_COMPONENT,
          listingInfo,
          uiConfig,
          true,
          maintenanceMode,
        ));

        if (appliedPresaleCoupons.length > 0) {
          dispatch(clearAlerts());

          await Promise.any(appliedPresaleCoupons.map(async (coupon) => {
            const isValid = await dispatch(validatePresaleCoupon({ coupon, ignoreError: true, reload: true }));
            if (isValid) {
              dispatch(submitPresaleCoupon(coupon))
                .catch(err => {
                  navigate('/promo');
                });
            }
          }));
        }

        // Re-validate the discount coupon against the new event so showSuccess accurately
        // reflects whether the code is valid here. This prevents auto-submit from firing
        // with a stale showSuccess=true from a prior event.
        if (activeDiscountCode && activeDiscountShowSuccess) {
          await dispatch(validateCoupon({ code: activeDiscountCode, submitAccessCoupon: false })).catch(() => {});
        }

        // Re-validate the access code against the new event. validateCoupon calls
        // submitPriceLevelCoupon internally when valid, so price levels unlock automatically.
        // Using validateCoupon (rather than submitPriceLevelCoupon directly) ensures we don't
        // apply an access code that isn't valid for this event.
        if (activeAccessCode) {
          await dispatch(validateCoupon({ code: activeAccessCode })).catch(() => {});
        }

        // set dynamic listing data
        dispatch(setDynamicListingId(listingId));
        dispatch(setDynamicListingSlug(listingSlug));
        dispatch(setDynamicListingEventId(eventId));
        dispatch(setDynamicListingInfo(listingInfo));
        dispatch(setDynamicListingUiConfig(uiConfig));
        dispatch(setDynamicListingMaintenanceMode(maintenanceMode));
        dispatch(loadedDynamicListing());
        dispatch(startCheckout());
        returnResult = true;
      }
    } catch (err) {
      returnResult = false;
      console.error('Error loading listing data: ' + err);
    }
    return returnResult;
  }
}

let dynamicLoadToken = 0;

export const cancelDynamicListingLoad = () => {
  return dispatch => {
    dynamicLoadToken++;
    dispatch(setDynamicListingLoading(false));
    dispatch(setCheckoutOverlay(false));
  }
}

export const openListingImmediately = (listingSlug, eventId) => {
  return async (dispatch, getState) => {
    if (getState().dynamicListing.isLoading) {
      return false;
    }

    const token = ++dynamicLoadToken;

    dispatch(setCheckoutOverlay(true));
    dispatch(setDynamicListingLoading(true));

    try {
      const loaded = await dispatch(loadListingDataIntoCheckout(listingSlug, eventId));

      // A dismiss or a newer open superseded this load. loadListingDataIntoCheckout's
      // startCheckout reopened the overlay, so keep it closed unless a newer load owns it.
      if (token !== dynamicLoadToken) {
        if (!getState().dynamicListing.isLoading) {
          dispatch(setCheckoutOverlay(false));
        }
        return loaded;
      }

      if (!loaded) {
        dispatch(setCheckoutOverlay(false));
      }
      return loaded;
    } finally {
      if (token === dynamicLoadToken) {
        dispatch(setDynamicListingLoading(false));
      }
    }
  }
}

export const rehydrateDynamicListingData = (languageParams = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    dispatch(loadListingDataIntoState(
      state.dynamicListing.listingId,
      state.dynamicListing.eventId,
      EMBED_TYPE_NO_COMPONENT,
      {...state.dynamicListing.listingInfo},
      {...state.dynamicListing.uiConfig},
      true,
      state.dynamicListing.maintenanceMode,
      languageParams
    ));
    dispatch(loadedDynamicListing());
  }
}
